import {
	type PlacementMessageFrontendFactory,
	type AnyProps,
	type NoProps,
	type PlacementRenderer,
	type DefaultMessagesResponse,
	type FrontendStage,
	type PlacementBrowserConfig,
} from '@post-office/message-maker-browser/types';

import { createPlacementFrontend } from '@post-office/message-maker-browser/create-placement-frontend';

import { Inline } from '@atlaskit/primitives/inline';

import { Stack } from '@atlaskit/primitives/stack';

import type { CustomRenderer } from '@post-office/message-maker-browser/types';

import { usePlacementApi } from '@post-office/use-placement-api';

import { useEffect, useState } from 'react';

type CustomDismissCallback = (messageInstanceId: string) => void;

// Subset of Popper.js placements supported by the spotlight popover.
// Extend this union if additional placements are needed in future.
export type PopoverPlacement = 'right-end' | 'left-end' | 'bottom-end' | 'top-end' | 'bottom-start';

// This type will be the contract between the message and the placement
export type placementRenderProps = {
	dismiss: () => void;
	messageInstanceId: string;
	onClick?: () => void;
	// Optional preferred placement for the spotlight popover. Defaults to
	// 'right-end'. Consumers should set this to 'left-end' (or another value)
	// when the component is rendered in a constrained container such as the
	// Sidebar where there is insufficient viewport space to the right.
	placement?: PopoverPlacement;
	// Identifier for the surface that mounted the placement (e.g.
	// 'issue-view-main', 'issue-view-sidebar'). Forwarded as an attribute on
	// every analytics event the message fires, including the initial "viewed"
	// event when the spotlight first appears, so consumers can distinguish
	// where the spotlight was rendered. Use `null` when no specific source is
	// applicable (matches the analytics attribute type).
	sourceComponent: string | null;
};

// We use the CustomRenderer type to tell the placement that these props are
// going to be used for rendering.
type placementDefinition = CustomRenderer<placementRenderProps>;

const placementConfig: PlacementBrowserConfig = {
	placementId: 'jira-issue-view-project-plan-twc-onboarding',
	options: {
		choreographer: {
			isDefaultEnabled: true,
		},
		hideInIsolatedCloud: false,
	},
};

const placement = {
	createFrontend: createPlacementFrontend<placementDefinition['frontend']>(placementConfig),
};

export const createFrontend: <U extends AnyProps = NoProps>(
	placementRenderer: PlacementRenderer<
		{
			render: FrontendStage<placementRenderProps>;
		},
		U
	>,
) => PlacementMessageFrontendFactory<
	{
		render: FrontendStage<placementRenderProps>;
	},
	U
> = placement.createFrontend;

type PlacementProps = {
	onClick?: () => void;
	placement?: PopoverPlacement;
	// Forwarded to every analytics event the message fires (clicked,
	// dismissed, viewed). Use this to identify the surface that mounted the
	// placement (e.g. 'issue-view-main' vs 'issue-view-sidebar'). Defaults to
	// `null` when not provided.
	sourceComponent?: string | null;
};

export const PlacementFrontend: PlacementMessageFrontendFactory<
	{
		render: FrontendStage<placementRenderProps>;
	},
	PlacementProps
> = createFrontend(
	(Messages) =>
		({ onClick, placement, sourceComponent = null }: PlacementProps) => {
			const [messages, setMessages] = useState<DefaultMessagesResponse['messages']>([]);

			const apiResponse = usePlacementApi<DefaultMessagesResponse>('/');

			useEffect(() => {
				const { error, data, isLoading } = apiResponse;

				if (isLoading) {
					return;
				}

				if (error) {
					throw error;
				}

				setMessages(data.messages);
			}, [apiResponse]);

			const handleDismiss: CustomDismissCallback = (messageInstanceId) => {
				// On dismiss we remove the message from the local state using the messageInstanceId.
				setMessages(messages.filter((message) => messageInstanceId !== message.messageInstanceId));
			};

			return (
				<Stack space="space.150">
					{!apiResponse.isLoading && (
						<Stack as="ul" space="space.200">
							{messages.map((props) => (
								<Inline as="li" key={props.messageInstanceId}>
									<Messages.render
										dismiss={() => handleDismiss(props.messageInstanceId)}
										onClick={onClick}
										placement={placement}
										sourceComponent={sourceComponent}
										{...props}
									/>
								</Inline>
							))}
						</Stack>
					)}
				</Stack>
			);
		},
);

export type PlacementFrontendProps = {
	onClick?: () => void;
};
